import {
  listSupportedLocales as publicListSupportedLocales,
  createLocale as publicCreateLocale,
  getLocale as publicGetLocale,
  updateLocale as publicUpdateLocale,
  deleteLocale as publicDeleteLocale,
  queryLocales as publicQueryLocales,
  typedQueryLocales as publicTypedQueryLocales,
  bulkCreateLocales as publicBulkCreateLocales,
  bulkUpdateLocales as publicBulkUpdateLocales,
  bulkDeleteLocales as publicBulkDeleteLocales,
  createNewPrimaryLocale as publicCreateNewPrimaryLocale,
  getNewPrimaryLocaleStatus as publicGetNewPrimaryLocaleStatus,
  setVisitorPrimaryLocale as publicSetVisitorPrimaryLocale,
} from './multilingual-locales-v2-locale-locales.public.js';
import { createRESTModule } from '@wix/sdk-runtime/rest-modules';
import { createEventModule } from '@wix/sdk-runtime/event-definition-modules';
import {
  BuildRESTFunction,
  MaybeContext,
  BuildEventDefinition,
} from '@wix/sdk-types';
import { HttpClient } from '@wix/sdk-types';
import { createQueryOverloadRouter } from '@wix/sdk-runtime/query-method-router';
import {
  LocalesQueryBuilder,
  QueryLocalesOptions,
  V2LocaleQuery,
  typedQueryLocales as universalTypedQueryLocales,
} from './multilingual-locales-v2-locale-locales.universal.js';
import { onLocaleCreated as publicOnLocaleCreated } from './multilingual-locales-v2-locale-locales.public.js';
import { onLocaleDeleted as publicOnLocaleDeleted } from './multilingual-locales-v2-locale-locales.public.js';
import { onLocaleMultilingualPrimaryLocaleChanged as publicOnLocaleMultilingualPrimaryLocaleChanged } from './multilingual-locales-v2-locale-locales.public.js';
import { onLocaleUpdated as publicOnLocaleUpdated } from './multilingual-locales-v2-locale-locales.public.js';

function customQueryLocales(httpClient: HttpClient) {
  const router = createQueryOverloadRouter({
    builderQueryFunction: (options?: QueryLocalesOptions) =>
      publicQueryLocales(httpClient)(options),
    typedQueryFunction: (query: V2LocaleQuery, options?: QueryLocalesOptions) =>
      publicTypedQueryLocales(httpClient)(query, options),
    hasOptionsParameter: true,
  });

  function overloadedQuery(
    query: V2LocaleQuery,
    options?: QueryLocalesOptions
  ): ReturnType<typeof universalTypedQueryLocales>;
  function overloadedQuery(options?: QueryLocalesOptions): LocalesQueryBuilder;
  function overloadedQuery(
    queryOrOptions?: V2LocaleQuery | QueryLocalesOptions,
    options?: QueryLocalesOptions
  ): any {
    return router(...arguments);
  }

  return overloadedQuery;
}

export const listSupportedLocales: MaybeContext<
  BuildRESTFunction<typeof publicListSupportedLocales> &
    typeof publicListSupportedLocales
> = /*#__PURE__*/ createRESTModule(publicListSupportedLocales);
export const createLocale: MaybeContext<
  BuildRESTFunction<typeof publicCreateLocale> & typeof publicCreateLocale
> = /*#__PURE__*/ createRESTModule(publicCreateLocale);
export const getLocale: MaybeContext<
  BuildRESTFunction<typeof publicGetLocale> & typeof publicGetLocale
> = /*#__PURE__*/ createRESTModule(publicGetLocale);
export const updateLocale: MaybeContext<
  BuildRESTFunction<typeof publicUpdateLocale> & typeof publicUpdateLocale
> = /*#__PURE__*/ createRESTModule(publicUpdateLocale);
export const deleteLocale: MaybeContext<
  BuildRESTFunction<typeof publicDeleteLocale> & typeof publicDeleteLocale
> = /*#__PURE__*/ createRESTModule(publicDeleteLocale);
export const bulkCreateLocales: MaybeContext<
  BuildRESTFunction<typeof publicBulkCreateLocales> &
    typeof publicBulkCreateLocales
> = /*#__PURE__*/ createRESTModule(publicBulkCreateLocales);
export const bulkUpdateLocales: MaybeContext<
  BuildRESTFunction<typeof publicBulkUpdateLocales> &
    typeof publicBulkUpdateLocales
> = /*#__PURE__*/ createRESTModule(publicBulkUpdateLocales);
export const bulkDeleteLocales: MaybeContext<
  BuildRESTFunction<typeof publicBulkDeleteLocales> &
    typeof publicBulkDeleteLocales
> = /*#__PURE__*/ createRESTModule(publicBulkDeleteLocales);
export const createNewPrimaryLocale: MaybeContext<
  BuildRESTFunction<typeof publicCreateNewPrimaryLocale> &
    typeof publicCreateNewPrimaryLocale
> = /*#__PURE__*/ createRESTModule(publicCreateNewPrimaryLocale);
export const getNewPrimaryLocaleStatus: MaybeContext<
  BuildRESTFunction<typeof publicGetNewPrimaryLocaleStatus> &
    typeof publicGetNewPrimaryLocaleStatus
> = /*#__PURE__*/ createRESTModule(publicGetNewPrimaryLocaleStatus);
export const setVisitorPrimaryLocale: MaybeContext<
  BuildRESTFunction<typeof publicSetVisitorPrimaryLocale> &
    typeof publicSetVisitorPrimaryLocale
> = /*#__PURE__*/ createRESTModule(publicSetVisitorPrimaryLocale);
export const queryLocales: MaybeContext<
  BuildRESTFunction<typeof customQueryLocales> & typeof customQueryLocales
> = /*#__PURE__*/ createRESTModule(customQueryLocales);
/**
 * Triggered when a secondary locale is created.
 */
export const onLocaleCreated: BuildEventDefinition<
  typeof publicOnLocaleCreated
> &
  typeof publicOnLocaleCreated = createEventModule(publicOnLocaleCreated);
/**
 * Triggered when a locale is deleted.
 */
export const onLocaleDeleted: BuildEventDefinition<
  typeof publicOnLocaleDeleted
> &
  typeof publicOnLocaleDeleted = createEventModule(publicOnLocaleDeleted);
/**
 * Triggered when a new primary locale is created.
 */
export const onLocaleMultilingualPrimaryLocaleChanged: BuildEventDefinition<
  typeof publicOnLocaleMultilingualPrimaryLocaleChanged
> &
  typeof publicOnLocaleMultilingualPrimaryLocaleChanged = createEventModule(
  publicOnLocaleMultilingualPrimaryLocaleChanged
);
/**
 * Triggered when a locale is updated.
 */
export const onLocaleUpdated: BuildEventDefinition<
  typeof publicOnLocaleUpdated
> &
  typeof publicOnLocaleUpdated = createEventModule(publicOnLocaleUpdated);

export {
  LocaleVisibility,
  UrlStructure,
  WebhookIdentityType,
  SortOrder,
  Status,
  State,
  SiteCreatedContext,
  Namespace,
  DeleteStatus,
  PlacementType,
  DayOfWeek,
  ResolutionMethod,
  ContactVerificationStatus,
} from './multilingual-locales-v2-locale-locales.universal.js';
export {
  V2Locale,
  UrlStructureOverride,
  CreateNewPrimaryLocalePayload,
  RawCreateLocaleRequest,
  RawCreateLocaleResponse,
  ChangeMainLocaleRequest,
  ChangeMainLocaleResponse,
  ForceDeleteLocaleRequest,
  ForceDeleteLocaleResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  RestoreInfo,
  EntityUpdatedEvent,
  EntityDeletedEvent,
  ActionEvent,
  MessageEnvelope,
  IdentificationData,
  IdentificationDataIdOneOf,
  AccountInfo,
  ListSupportedLocalesRequest,
  ListSupportedLocalesResponse,
  SupportedLocale,
  RegionOptions,
  InvalidateCache,
  InvalidateCacheGetByOneOf,
  App,
  Page,
  URI,
  File,
  CustomTag,
  Pages,
  URIs,
  CreateLocaleRequest,
  CreateLocaleResponse,
  GetLocaleRequest,
  GetLocaleResponse,
  UpdateLocaleRequest,
  UpdateLocaleResponse,
  DeleteLocaleRequest,
  DeleteLocaleResponse,
  QueryLocalesRequest,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Sorting,
  CursorPaging,
  QueryLocalesResponse,
  CursorPagingMetadata,
  Cursors,
  BulkCreateLocalesRequest,
  BulkCreateLocalesResponse,
  ItemMetadata,
  ApplicationError,
  BulkLocaleResult,
  BulkActionMetadata,
  BulkUpdateLocalesRequest,
  MaskedLocale,
  BulkUpdateLocalesResponse,
  BulkUpdateLocalesResponseBulkLocaleResult,
  BulkDeleteLocalesRequest,
  BulkDeleteLocalesResponse,
  BulkDeleteLocalesResponseBulkLocaleResult,
  CreateNewPrimaryLocaleRequest,
  CreateNewPrimaryLocaleResponse,
  GetNewPrimaryLocaleStatusRequest,
  GetNewPrimaryLocaleStatusResponse,
  GetNewPrimaryLocaleStatusResponseDataOneOf,
  FinishSuccessfulData,
  FinishWithErrorData,
  SetVisitorPrimaryLocaleRequest,
  SetVisitorPrimaryLocaleResponse,
  MetaSiteSpecialEvent,
  MetaSiteSpecialEventPayloadOneOf,
  Asset,
  SiteCreated,
  SiteTransferred,
  SiteDeleted,
  DeleteContext,
  SiteUndeleted,
  SitePublished,
  SiteUnpublished,
  SiteMarkedAsTemplate,
  SiteMarkedAsWixSite,
  ServiceProvisioned,
  ServiceRemoved,
  SiteRenamed,
  SiteHardDeleted,
  NamespaceChanged,
  StudioAssigned,
  StudioUnassigned,
  SiteUrlChanged,
  SitePurgedExternally,
  OdeditorAssigned,
  OdeditorUnassigned,
  PicassoAssigned,
  PicassoUnassigned,
  WixelAssigned,
  WixelUnassigned,
  StudioTwoAssigned,
  StudioTwoUnassigned,
  UserDomainMediaEnabled,
  UserDomainMediaDisabled,
  EditorlessAssigned,
  EditorlessUnassigned,
  Empty,
  SitePropertiesUpdated,
  SitePropertiesEvent,
  Properties,
  Categories,
  Locale,
  Address,
  AddressHint,
  GeoCoordinates,
  BusinessSchedule,
  TimePeriod,
  SpecialHourPeriod,
  Multilingual,
  SupportedLanguage,
  ConsentPolicy,
  DomainChangedEvent,
  DomainChangedEventPayloadOneOf,
  Registered,
  RegistrationCancelled,
  ConnectedToSite,
  DisconnectedFromSite,
  RegistrationExtended,
  OwnerChanged,
  PrivacyChanged,
  ManagementTypeChanged,
  BillingSubscriptionCancelled,
  ConnectionVerified,
  ConnectionLost,
  ExpirationDateChanged,
  PropagationStarted,
  VerificationStarted,
  ContactVerificationStatusChanged,
  Expired,
  GraceEnded,
  RedemptionEnded,
  DnssecChanged,
  PriceIncreaseProcessed,
  BaseEventMetadata,
  EventMetadata,
  AccountInfoMetadata,
  LocaleCreatedEnvelope,
  LocaleDeletedEnvelope,
  LocaleMultilingualPrimaryLocaleChangedEnvelope,
  LocaleUpdatedEnvelope,
  ListSupportedLocalesOptions,
  UpdateLocale,
  QueryLocalesOptions,
  LocalesQueryResult,
  LocalesQueryBuilder,
  V2LocaleQuerySpec,
  BulkCreateLocalesOptions,
  BulkUpdateLocalesOptions,
} from './multilingual-locales-v2-locale-locales.universal.js';
export { utils } from './multilingual-locales-v2-locale-locales.universal.js';
export {
  LocaleVisibilityWithLiterals,
  UrlStructureWithLiterals,
  WebhookIdentityTypeWithLiterals,
  SortOrderWithLiterals,
  StatusWithLiterals,
  StateWithLiterals,
  SiteCreatedContextWithLiterals,
  NamespaceWithLiterals,
  DeleteStatusWithLiterals,
  PlacementTypeWithLiterals,
  DayOfWeekWithLiterals,
  ResolutionMethodWithLiterals,
  ContactVerificationStatusWithLiterals,
  CreateNewPrimaryLocaleApplicationErrors,
  SetVisitorPrimaryLocaleApplicationErrors,
  CommonQueryWithEntityContext,
  V2LocaleQuery,
} from './multilingual-locales-v2-locale-locales.universal.js';
